/**
 * FeatureGate Component
 * Renders children only if the specified feature is enabled in the license
 * Shows "feature not available" message otherwise
 */

import { useLicense } from '../../hooks/useLicense';
import { isFeatureEnabled } from '../../services/licenseService';

interface FeatureGateProps {
    featureId: string;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

export function FeatureGate({ featureId, children, fallback }: FeatureGateProps) {
    const { license, isLoading } = useLicense();

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent"></div>
                    <p className="mt-4 text-lg text-gray-700">Checking feature access...</p>
                </div>
            </div>
        );
    }

    // Check if feature is enabled
    const featureAvailable = isFeatureEnabled(featureId, license);

    if (!featureAvailable) {
        // Use custom fallback if provided, otherwise show default message
        if (fallback) {
            return <>{fallback}</>;
        }

        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                    <div className="text-center">
                        <svg
                            className="mx-auto h-16 w-16 text-yellow-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                        </svg>
                        <h1 className="mt-6 text-2xl font-bold text-gray-900">
                            Feature Not Available
                        </h1>
                        <p className="mt-4 text-gray-600">
                            This feature (<strong>{featureId}</strong>) is not included in your current Injio NG license.
                        </p>
                        <p className="mt-4 text-sm text-gray-500">
                            Please contact your administrator to upgrade your license and unlock this feature.
                        </p>
                        <div className="mt-6">
                            <a
                                href="/dashboard"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                                Return to Dashboard
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Feature is enabled - render children
    return <>{children}</>;
}
